import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * CrossLinkStrip
 * --------------
 * A compact, navy-led "Related guides" grid used across the builder-selection
 * cluster to strengthen topical-cluster ranking signals in Google.
 *
 * Design: matches the existing site (Inter, navy #0a2540 accents, hairline
 * borders, no emoji). Sits on white; use inside a Section wrapper.
 *
 * Props:
 *   eyebrow  small caps label above the heading (default: "Related guides")
 *   heading  short H2 (default: "Keep exploring")
 *   items    array of { title, description, href, cta }
 *   testId   data-testid root (default: "cross-link-strip")
 */
export const CrossLinkStrip = ({
  eyebrow = 'Related guides',
  heading = 'Keep exploring',
  items = [],
  testId = 'cross-link-strip',
}) => {
  if (!items || items.length === 0) return null;

  return (
    <section
      className="py-12 sm:py-16 lg:py-20 bg-white border-t border-gray-100"
      data-testid={testId}
    >
      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#0a2540]">
            {heading}
          </h2>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`${testId}-item-${item.href.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}`}
              className="group flex flex-col justify-between h-full p-5 sm:p-6 rounded-2xl border border-gray-200 bg-white hover:border-[#0a2540] hover:shadow-sm transition-all duration-200"
            >
              <div>
                <div className="text-[15px] font-semibold text-[#0a2540] leading-snug mb-2 group-hover:text-[#1d8efe] transition-colors">
                  {item.title}
                </div>
                <p className="text-[13.5px] text-gray-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#1d8efe]">
                {item.cta || 'Read the guide'}
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CrossLinkStrip;
